import React, { useState } from 'react'

import {
  Box,
  Button,
  Card,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  TextField,
  Typography,
} from '@material-ui/core'
import { Skeleton } from '@material-ui/lab'
import { Plus as PlusIcon, Trash2 as Trash2Icon } from 'react-feather'
import { isEmpty } from 'lodash'

import { ConfirmationDialog } from 'components'
import useFetch from 'hooks/useFetch'
import useSnackbar from 'hooks/useSnackbar'
import * as service from 'service'
import helpers from 'helpers'

import useStyles from './styles'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const NotificationEmailsCard = ({ courseId, isEdit }) => {
  const classes = useStyles()
  const snackbar = useSnackbar()

  const [newEmail, setNewEmail] = useState('')
  const [isAdding, setIsAdding] = useState(false)
  const [emailToDelete, setEmailToDelete] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const { response, isLoading, refresh } = useFetch(
    service.dponet.trainingCourse.notificationEmails,
    { courseId },
    [courseId],
  )

  const notificationEmails =
    response?.data?.courseCompanyNotificationEmails ?? []

  const handleAdd = async () => {
    if (!EMAIL_REGEX.test(newEmail)) {
      snackbar.open({ message: 'Informe um e-mail válido.', variant: 'error' })
      return
    }

    setIsAdding(true)
    try {
      await service.dponet.trainingCourse.createNotificationEmail({
        courseId,
        email: newEmail,
      })
      setNewEmail('')
      refresh()
      snackbar.open({
        message: 'E-mail adicionado com sucesso.',
        variant: 'success',
      })
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(error?.response?.data?.error),
        variant: 'error',
      })
    } finally {
      setIsAdding(false)
    }
  }

  const handleDelete = async () => {
    setIsDeleting(true)
    try {
      await service.dponet.trainingCourse.deleteNotificationEmail({
        courseId,
        id: emailToDelete.id,
      })
      refresh()
      snackbar.open({
        message: 'E-mail removido com sucesso.',
        variant: 'success',
      })
      setEmailToDelete(null)
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(error?.response?.data?.error),
        variant: 'error',
      })
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <Box className={classes.emphasisColor}>
      <Box p={2}>
        <Typography variant="body2">E-mails</Typography>
        <Typography variant="body2" color="textSecondary">
          Toda comunicação ou notificação sobre o curso será enviada para os
          e-mails cadastrados abaixo.
        </Typography>
      </Box>
      {isEdit && (
        <Box className={classes.addRow}>
          <TextField
            variant="outlined"
            size="small"
            placeholder="email@empresa.com"
            fullWidth
            value={newEmail}
            onChange={(event) => setNewEmail(event.target.value)}
            onKeyPress={(event) => {
              if (event.key !== 'Enter') return
              event.preventDefault()
              handleAdd()
            }}
          />
          <Button
            type="button"
            variant="contained"
            color="primary"
            startIcon={<PlusIcon />}
            onClick={handleAdd}
            disabled={isAdding || isEmpty(newEmail)}
          >
            Adicionar
          </Button>
        </Box>
      )}

      {isLoading ? (
        <Box className={classes.skeletonList}>
          {[1, 2, 3].map((key) => (
            <Skeleton key={key} width="100%" height={40} variant="text" />
          ))}
        </Box>
      ) : isEmpty(notificationEmails) ? (
        <Box className={classes.emphasisColor} px={2}>
          <Typography
            variant="body2"
            color="textSecondary"
            className={classes.emptyMessage}
          >
            Nenhum e-mail cadastrado.
          </Typography>
        </Box>
      ) : (
        <List>
          <Box px={2}>
            {notificationEmails.map((notificationEmail) => (
              <ListItem key={notificationEmail.id} divider>
                <ListItemText primary={notificationEmail.email} />
                {isEdit && (
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() => setEmailToDelete(notificationEmail)}
                    >
                      <Trash2Icon size={18} />
                    </IconButton>
                  </ListItemSecondaryAction>
                )}
              </ListItem>
            ))}
          </Box>
        </List>
      )}
      {emailToDelete && (
        <ConfirmationDialog
          open={!!emailToDelete}
          setOpen={() => setEmailToDelete(null)}
          text={`Remover o e-mail "${emailToDelete?.email}"?`}
          actionAcceptButton={handleDelete}
          loading={isDeleting}
          variant="error"
          textButton="Remover"
        />
      )}
    </Box>
  )
}

export default NotificationEmailsCard
